import { cache } from 'react';

import {
  fetchSiteConfig,
  fetchSitePages,
  fetchPageDetails,
  fetchWidgetDetails,
  fetchTranslations,
  fetchAlbum,
  fetchPlaylist,
  fetchVideo,
  fetchCollection,
  fetchCollectionContents,
  fetchPodcast,
  fetchArticle,
  fetchGallery,
  fetchQuiz,
  fetchGame,
} from './resources';

import type { AccessScope } from '@/lib/auth';

import type { UUID } from '@/types';

export const fetchSiteConfigCached = cache(async (tenantId?: string) =>
  fetchSiteConfig(tenantId),
);

export const fetchSitePagesCached = cache(
  async (
    siteId: UUID | undefined,
    tenantId: string,
    scope: AccessScope = 'public',
    locale?: string,
  ) => fetchSitePages(siteId, tenantId, { scope, locale }),
);

export const fetchPageDetailsCached = cache(
  async (
    siteId: UUID | undefined,
    pageId: number | undefined,
    tenantId: string,
    scope: AccessScope = 'public',
    locale?: string,
  ) => fetchPageDetails(siteId, pageId, tenantId, { scope, locale }),
);

export const fetchWidgetDetailsCached = cache(
  async (
    siteId: UUID | undefined,
    widgetId: number | undefined,
    tenantId: string,
    scope: AccessScope = 'public',
    locale?: string,
  ) => fetchWidgetDetails(siteId, widgetId, tenantId, { scope, locale }),
);

export const fetchTranslationsCached = cache(
  async (locale: string, tenantId?: string) =>
    fetchTranslations(locale, tenantId),
);

export const fetchAlbumCached = cache(
  async (
    slugOrId: string | number,
    locale: string,
    tenantId?: string,
    scope?: AccessScope,
  ) => fetchAlbum({ slugOrId, locale, tenantId, scope }),
);

export const fetchArticleCached = cache(
  async (
    slugOrId: string | number,
    locale: string,
    tenantId?: string,
    scope?: AccessScope,
  ) => fetchArticle({ slugOrId, locale, tenantId, scope }),
);

export const fetchCollectionCached = cache(
  async (
    slugOrId: string | number,
    locale: string,
    universe?: string,
    tenantId?: string,
    scope?: AccessScope,
  ) => fetchCollection({ slugOrId, locale, universe, tenantId, scope }),
);

export const fetchCollectionContentsCached = cache(
  async (
    collectionId: string | number,
    locale: string,
    universe?: string,
    scope?: AccessScope,
  ) => fetchCollectionContents({ collectionId, locale, universe, scope }),
);

export const fetchGalleryCached = cache(
  async (
    slugOrId: string | number,
    locale: string,
    tenantId?: string,
    scope?: AccessScope,
  ) => fetchGallery({ slugOrId, locale, tenantId, scope }),
);

export const fetchPodcastCached = cache(
  async (
    slugOrId: number,
    locale: string,
    tenantId?: string,
    scope?: AccessScope,
  ) => fetchPodcast({ slugOrId, locale, tenantId, scope }),
);

export const fetchPlaylistCached = cache(
  async (
    slugOrId: string | number,
    locale: string,
    tenantId?: string,
    scope?: AccessScope,
  ) => fetchPlaylist({ slugOrId, locale, tenantId, scope }),
);

export const fetchVideoCached = cache(
  async (
    slugOrId: string | number,
    locale: string,
    tenantId?: string,
    scope?: AccessScope,
  ) => fetchVideo({ slugOrId, locale, tenantId, scope }),
);

export const fetchQuizCached = cache(
  async (
    slugOrId: string | number,
    locale: string,
    tenantId?: string,
    scope?: AccessScope,
  ) => fetchQuiz({ slugOrId, locale, tenantId, scope }),
);

export const fetchGameCached = cache(
  async (
    slugOrId: string | number,
    locale: string,
    tenantId?: string,
    scope?: AccessScope,
  ) => fetchGame({ slugOrId, locale, tenantId, scope }),
);
